import {
  Elements,
  IbanElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import PropTypes from 'prop-types'
import React, { useState } from 'react'

// Custom styling can be passed to options when creating an Element.
const IBAN_ELEMENT_OPTIONS = {
  supportedCountries: ['SEPA'],
  style: {
    base: {
      color: '#32325d',
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
      ':-webkit-autofill': {
        color: '#32325d',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
      ':-webkit-autofill': {
        color: '#fa755a',
      },
    },
  },
}

const NoleggiaIbanElement = ({
  nome,
  cognome,
  email,
  onError,
  setIsFetching,
  onConfirmSepaDebitSetup,
  transazioniCreateSepaIntent,
  stripeSepaIntent,
  storeDumpItForce,
}) => {
  const [error, setError] = useState(null)
  const [setupIntent, setSetupIntent] = useState(null)

  const stripe = useStripe()
  const elements = useElements()

  const GREY600 = '#6B6B6B'

  const stripeErrorToString = err => `${err.code} ${err.type} ${err.message}`

  const handleChange = event => {
    if (setupIntent) {
      // notifico che si sta cambiando iban
      setSetupIntent(null)
      onConfirmSepaDebitSetup(null)
    }

    // Handle real-time validation errors from the iban Element.
    if (event.error) {
      setError(event.error.message)
      onError(stripeErrorToString(event.error))
    } else {
      setError(null)
    }
  }

  function confirmSepaDebitSetup(setupIntentClientSecret) {
    try {
      const ibanElement = elements.getElement(IbanElement)
      stripe
        .confirmSepaDebitSetup(setupIntentClientSecret, {
          payment_method: {
            sepa_debit: ibanElement,
            billing_details: {
              name: `${nome} ${cognome}`,
              email,
            },
          },
        })
        .then(result => {
          if (result.error) {
            // Inform the user if there was an error.
            setError(result.error.message)
            onError(stripeErrorToString(result.error))
            setIsFetching(false)
          } else {
            setError(null)
            // Normally you would send the PaymentMethod to your server.
            // In this demo we store it in state and display it.
            setSetupIntent(result.setupIntent)
            onConfirmSepaDebitSetup(result.setupIntent)
            setIsFetching(false)
          }
        })
        .catch(err => {
          onError(err)
          setIsFetching(false)
        })
    } catch (e) {
      onError(e)
      setIsFetching(false)
    }
  }

  // Handle form submission.
  const handleSubmit = event => {
    event.preventDefault()
    setIsFetching(true)

    if (!stripeSepaIntent) {
      transazioniCreateSepaIntent().then(json => {
        storeDumpItForce()
        const setupIntentClientSecret = json.result.client_secret_key
        confirmSepaDebitSetup(setupIntentClientSecret)
      })
    } else {
      // nello store avevo gia' la secret
      confirmSepaDebitSetup(stripeSepaIntent.client_secret_key)
    }
  }

  return (
    <React.Fragment>
      <li className={`${error ? 'is-error' : ''} wide`}>
        <div className="relative-input">
          <div>
            <p className="label">IBAN</p>
            <div
              style={{
                border: `1px solid ${GREY600}`,
                borderRadius: '3px',
                padding: '15px',
              }}
            >
              <IbanElement
                id="iban-element"
                onChange={handleChange}
                options={IBAN_ELEMENT_OPTIONS}
              />
            </div>
          </div>
        </div>
      </li>
      <li className="wide">
        <div id="mandate-acceptance">
          La sottoscrizione del presente mandato comporta (A) l’autorizzazione a
          brumbrum S.p.A. e Stripe, il nostro prestatore di servizi di
          pagamento, a richiedere alla banca del debitore l’addebito del suo
          conto e (B) l’autorizzazione alla banca del debitore di procedere a
          tale addebito conformemente alle disposizioni impartite da brumbrum
          S.p.A. e Stripe. Il debitore ha diritto di ottenere il rimborso dalla
          propria Banca secondo gli accordi ed alle condizioni che regolano il
          rapporto con quest’ultima. Se del caso, il rimborso deve essere
          richiesto nel termine di 8 settimane a decorrere dalla data di
          addebito in conto.
        </div>
      </li>

      <li className="wide buttons">
        <a className={`btn btn-primary js-next-step`} onClick={handleSubmit}>
          <span>Prosegui</span>
        </a>
      </li>
    </React.Fragment>
  )
}

// Setup Stripe.js and the Elements provider
const stripePromise = loadStripe(window.brumbrum.stripe_key)

const NoleggiaIbanElementWrapper = props => (
  <Elements stripe={stripePromise}>
    <NoleggiaIbanElement {...props} />
  </Elements>
)

NoleggiaIbanElementWrapper.propTypes = {
  nome: PropTypes.string.isRequired,
  cognome: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  onError: PropTypes.func.isRequired,
  onConfirmSepaDebitSetup: PropTypes.func.isRequired,
  setIsFetching: PropTypes.func.isRequired,
  transazioniCreateSepaIntent: PropTypes.func.isRequired,
  storeDumpItForce: PropTypes.func.isRequired,
  stripeSepaIntent: PropTypes.object,
}

export default NoleggiaIbanElementWrapper
